/**
 * Fullscreen sorpresa: $99.99, solo botón comprar, fondo = Mux playback ID.
 * Route: /sorpresa
 * Tracking: metadata source from BRAND_SOURCE_KEY.
 *
 * Playback ID: primero desde /api/public-playback-ids (runtime, key SORPRESA_PLAYBACK_ID en Vercel),
 * luego VITE_SORPRESA_PLAYBACK_ID (build time), luego fallback local.
 */

import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { MembershipFloatingCta } from "@/components/MembershipFloatingCta";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";

const FALLBACK_PLAYBACK_ID =
  (import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string | undefined)?.trim() ||
  (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() ||
  "";

/** Valor en build time (VITE_SORPRESA_PLAYBACK_ID) o fallback */
const buildTimePlaybackId =
  (import.meta.env.VITE_SORPRESA_PLAYBACK_ID as string)?.trim() || FALLBACK_PLAYBACK_ID;

export default function FullscreenSorpresa() {
  const [playbackId, setPlaybackId] = useState(buildTimePlaybackId);
  // videoPlaying: oculta el MuxPosterBackdrop con fade cuando el video
  // arranca realmente. Coherente con DualCTAVideoLanding. Audit 2026-06-14:
  // antes el visitor veía cuadrado negro hasta que el HLS resolvía (1-3s en
  // 3G / IG in-app); ahora gradient + LQIP + poster en ~50-150 ms.
  const [videoPlaying, setVideoPlaying] = useState(false);
  const { setTemplateName } = useCurrentTemplate();
  const { country } = useTemplateSelection();
  const { search } = useLocation();

  // /sorpresa es independiente: no aplicar BaseTemplate ni otro template de la home
  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, []);

  // Runtime: usar key definida en Vercel (SORPRESA_PLAYBACK_ID) sin necesidad de redeploy
  useEffect(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (!base) return;
    fetch(`${base}/api/public-playback-ids`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { sorpresa?: string } | null) => {
        const id = data?.sorpresa?.trim();
        if (id) setPlaybackId(id);
      })
      .catch(() => {});
  }, []);

  // Argentina: el pack "sorpresa" no tiene checkout MercadoPago (el player solo
  // soporta Basic/channel_invite en AR → mostraría un error sin salida). Redirigimos
  // al flujo de donación AR (MercadoPago/ARS) de /sorprendeme, preservando query/UTMs.
  if (country === "AR") {
    return <Navigate to={`/sorprendeme${search}`} replace />;
  }

  return (
    <div className="relative w-full h-dvh max-h-dvh overflow-hidden bg-black">
      <MuxPosterBackdrop
        key={`poster-${playbackId}`}
        playbackId={playbackId}
        fitMode="smartcrop"
        hidden={videoPlaying}
      />
      <MuxVideoPlayerWithCTA
        playbackId={playbackId}
        overridePackId="sorpresa"
        variant="fullscreen"
        ctaLabel="Descubrir"
        onPlay={() => setVideoPlaying(true)}
      />
      <MembershipFloatingCta />
    </div>
  );
}
